import Mark from 'mark.js';
import {plural} from 'pluralize';
import React, {Component, createRef, RefObject} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {searchableAttribute} from '../../../srcCommon/inboxes/search/searchAttributesHelpers';
import {ModifiersEnum} from '../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {isSearchModifierToken, splitSearchQuery} from '../../../srcCommon/inboxes/search/searchQueryHelpers';

/*
 * Constants.
 */

const searchParentAttribute = 'data-searchparent';
const markJsAttribute = 'data-markjs';
const markJsLimiters = ':;.,-_(){}[]!?@\'"+='.split('');

/*
 * Props.
 */

interface HighlighterProps {
  id: string;
  query: string;
  className?: string;
  onResults?: (arg: ReadonlyArray<Element>) => void;
  usePartialMatch?: boolean;
  acceptedModifiers?: ReadonlyArray<ModifiersEnum>;
  eachMatch?: (element: Element) => void;
  addRefreshCb?: (key: string, fn: () => void) => void;
  removeRefreshCb?: (key: string) => void;
}

/*
 * Style.
 */

const StyledInvisibleDiv = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  top: 0;
  left: 0;
`;

/*
 * Component.
 */

export class Highlighter extends Component<HighlighterProps> {
  constructor(props: HighlighterProps) {
    super(props);

    this.previousTargetCount = 0;
    this.highlighterRef = createRef<HTMLDivElement>();
    this.elementName = `mark-${props.id}`;
    this.regExpCreator = new MarkRegExpCreator(
      markJsLimiters,
      props.acceptedModifiers || [],
      props.usePartialMatch,
    );
  }

  private searchParent?: Element;
  private mark?: Mark;
  private previousTargetCount: number;
  private readonly highlighterRef: RefObject<HTMLDivElement>;
  private readonly elementName: string;
  private regExpCreator: MarkRegExpCreator;

  /*
   * Lifecycle.
   */

  componentDidMount() {
    // Find the parent element to search into.
    const currentHighlighter = this.highlighterRef.current;
    if (!currentHighlighter) {
      return;
    }

    this.searchParent = currentHighlighter.closest(`[${searchParentAttribute}]`) || undefined;
    this.updateMark();
    if (this.props.addRefreshCb) {
      this.props.addRefreshCb(this.props.id, () => {
        this.updateMark();
      });
    }
  }

  componentDidUpdate(previousProps: Readonly<HighlighterProps>) {
    this.updateMark(previousProps.query);
  }

  componentWillUnmount() {
    this.removeMark();
    if (this.props.removeRefreshCb) {
      this.props.removeRefreshCb(this.props.id);
    }
  }

  /*
   * Event handlers.
   */

  private readonly onMarkDone = () => {
    const document = this.highlighterRef.current && this.highlighterRef.current.ownerDocument;
    if (!document || !this.mark || !this.props.onResults) {
      return;
    }

    const results = Array.from(document.querySelectorAll(`${this.elementName}[${markJsAttribute}]`));
    this.props.onResults(results);
  };

  /*
   * Mark logic.
   */

  private updateMark(previousQuery?: string) {
    if (!this.searchParent) {
      return;
    }

    const targets: ReadonlyArray<HTMLElement> = Array.from(
      this.searchParent.querySelectorAll(this.regExpCreator.targetsQuery),
    );
    const targetCount = targets.length;

    // If neither the number of targets nor the query changed, nothing to do.
    if (targetCount === this.previousTargetCount && this.props.query === previousQuery) {
      return;
    }

    // Store the number of results if it changed.
    this.previousTargetCount = targetCount;

    // Clear the previous search, if any.
    this.removeMark();

    // If no query was provided, nothing to do.
    const {className, query} = this.props;
    if (!query) {
      return;
    }

    // Otherwise, create a new mark instance.
    //
    // NOTE: we pass an array to `Mark()` here because when given a NodeList, it will try to verify
    // it's a NodeList by using `NodeList.prototype.isPrototypeOf(foo)` and that returns `false`
    // if `NodeList` and `foo` aren't from the same JS context. This then will end up in a crash
    // inside mark.js because the internals of mark.js will try to do `document.createNodeIterator`
    // on a NodeList, which obviously fails.

    const regExpQuery = this.regExpCreator.create(query);

    const eachCallback = (element: Element) => {
      this.regExpCreator.maybeRemoveMark(element);
      this.props.eachMatch?.(element);
    };

    this.mark = new Mark(targets);
    this.mark.markRegExp(regExpQuery, {
      className,
      element: this.elementName,
      each: eachCallback,
      done: this.onMarkDone,
    });
  }

  private removeMark() {
    if (!this.mark) {
      return;
    }

    this.mark.unmark({element: this.elementName});
    this.mark = undefined;
  }

  /*
   * Render.
   */

  render() {
    return (
      <StyledInvisibleDiv
        {...buildInteractionIdProps('highlighter')}
        ref={this.highlighterRef}
        className={this.props.className}
      />
    );
  }
}

/*
 * Helpers.
 */
/* eslint-disable class-methods-use-this */

class MarkRegExpCreator {
  private acceptedModifiers: ReadonlyArray<ModifiersEnum>;
  private usedModifiers: Map<string, Set<ModifiersEnum>>;

  private usePartialMatch: boolean;

  private regExpStart: string;
  private regExpEnd: string;

  targetsQuery: string;

  constructor(
    limiters: ReadonlyArray<string>,
    acceptedModifiers: ReadonlyArray<ModifiersEnum>,
    usePartialMatch?: boolean,
  ) {
    this.acceptedModifiers = acceptedModifiers;
    this.usedModifiers = new Map();

    this.usePartialMatch = Boolean(usePartialMatch);

    const [regExpStart, regExpEnd] = ['^', '$'].map(
      (t) => `?=${t}|\\s|\\b|${limiters.map(this.escape).join('|')}`,
    );
    this.regExpStart = regExpStart;
    this.regExpEnd = regExpEnd;

    this.targetsQuery = this.generateTargetsQuery();
  }

  private generateTargetsQuery() {
    const modifiersSearchableAttributes = this.acceptedModifiers.map(
      (modifier) => `[${searchableAttribute}-${modifier}]`,
    );

    return `${[`[${searchableAttribute}]`, ...modifiersSearchableAttributes].join(', ')}`;
  }

  // This method is responsible for creating the RegExp that will be used in Mark.js from a search query.
  create(query: string) {
    // First, clear `usedModifiers`.
    this.usedModifiers.clear();

    const separatedKeywords = this.getSeparatedKeywords(query);

    /**
     * If `usePartialMatch` is true, we want to match consecutive words.
     * This way, if the query is `another te`, it won't highlight `another` not followed by ` te`.
     * To do so, we have to join each keyword with a space.
     *
     * If `usePartialMatch` is false, then we should highlight both `another` and `fe`.
     * To do so, we have to join each keyword with a `|`.
     */
    const keywordsJoiner = this.usePartialMatch ? ' ' : '|';

    const stringifiedQuery = this.handleStemming(separatedKeywords).join(keywordsJoiner);

    // Reuse the same patterns as Mark.js in its RegExpCreator (`partially` or `exactly` accuracy).
    const regExp = this.usePartialMatch
      ? `()(${stringifiedQuery})`
      : `(${this.regExpStart})(${stringifiedQuery})(${this.regExpEnd})`;

    // Use `global`, `multiline` and `ignore case` flags.
    return new RegExp(regExp, 'gmi');
  }

  private populateUsedModifiers(modifier: ModifiersEnum, value: string) {
    const sanitizedValue = this.sanitize(value);

    const existingModifiers = this.usedModifiers.get(sanitizedValue) || new Set();
    existingModifiers.add(modifier);

    this.usedModifiers.set(sanitizedValue, existingModifiers);
  }

  private getSeparatedKeywords(query: string) {
    const cleanQuery = query.trim().toLowerCase();
    const queryParts = splitSearchQuery(cleanQuery);

    return queryParts
      .filter(({token}) => {
        // Remove parts for which token is not defined (i.e. spaces).
        if (!token || !token.value) {
          return false;
        }

        // If this part is not a modifier, return true.
        if (!isSearchModifierToken(token)) {
          return true;
        }

        // If this part is a modifier which is accepted, populate `usedModifiers` and return true.
        if (this.acceptedModifiers.includes(token.modifier)) {
          this.populateUsedModifiers(token.modifier, token.value);
          return true;
        }

        // Otherwise, return false.
        return false;
      })
      .map(({token}) => this.escape(token?.value || ''));
  }

  // We need to remove quotes to make sure that if someone is looking for "fire", we will highlight `fire`.
  private sanitize(str: string) {
    return str.replace(/"|'/g, '');
  }

  private escape(str: string) {
    // eslint-disable-next-line no-useless-escape
    return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
  }

  private isExactMatch(str: string) {
    // An exact match starts and ends with a double quote.
    return str[0] === '"' && str[str.length - 1] === '"';
  }

  private handleStemming(terms: Array<string>) {
    return terms.map((term) =>
      // As `term` may be made up of several words, we need to stem each of them.
      term
        .split(' ')
        .reduce((acc, part) => {
          const sanitizedPart = this.sanitize(part);
          if (this.isExactMatch(part)) {
            // No stemming for exact matches ("word").
            return [...acc, `(${sanitizedPart})`];
          }
          // Populate stemmed term and plural only if the last char is a letter.
          const currentStemming = /[a-zA-Z]/.test(sanitizedPart[sanitizedPart.length - 1])
            ? [sanitizedPart, this.simulateStemming(sanitizedPart), plural(sanitizedPart)].filter(
                (item, index, arr) => arr.indexOf(item) === index,
              )
            : [sanitizedPart];

          // Wrap original term, stemmed term and plural inside brackets.
          return [...acc, `(${currentStemming.join('|')})`];
        }, [] as Array<string>)
        .join(' '),
    );
  }

  /**
   * Compute string's stemming with the "S-Stemming" algorithm.
   * Returns the stemmed string if need be, otherwise the original one.
   * Mimics the Lucene implementation:
   * https://github.com/apache/lucene-solr/blob/master/lucene/analysis/common/src/java/org/apache/lucene/analysis/en/EnglishMinimalStemmer.java
   */
  private simulateStemming(term: string) {
    const {length} = term;

    if (length < 3 || term[length - 1] !== 's') {
      return term;
    }

    switch (term[length - 2]) {
      case 'u':
      case 's':
        return term;
      case 'e':
        if (length > 3 && term[length - 3] === 'i' && term[length - 4] !== 'a' && term[length - 4] !== 'e') {
          return `${term.slice(0, -3)}y`;
        }
        if (
          term[length - 3] === 'i' ||
          term[length - 3] === 'a' ||
          term[length - 3] === 'o' ||
          term[length - 3] === 'e'
        ) {
          // Intentional fallthrough.
          return term;
        }
        return term.slice(0, -1);
      default: {
        // By default, return the string without the trailing `s`.
        return term.slice(0, -1);
      }
    }
  }

  maybeRemoveMark(elem: Element) {
    const {acceptedModifiers, usedModifiers} = this;

    // If `acceptedModifiers` is empty or there are no modifiers, no need to remove anything..
    if (acceptedModifiers.length > 0 && usedModifiers.size > 0) {
      for (const value of usedModifiers.keys()) {
        if (elem?.textContent && elem.textContent.toLowerCase().includes(value)) {
          // Find the closest parent that has the `searchableAttribute` attribute.
          const dataSearchableClosestParent = elem.closest(`[${searchableAttribute}]`);

          const modifiers = usedModifiers.get(value) || new Set<ModifiersEnum>();

          // Check if the closest parent also has one of the modifier for that value.
          const shouldKeepMark = [...modifiers].some((modifier) =>
            dataSearchableClosestParent?.getAttribute(`${searchableAttribute}-${modifier}`),
          );

          // If this is not the case, replace the element by what's inside.
          if (!shouldKeepMark) {
            elem.replaceWith(elem.innerHTML);
          }

          // Break the loop because we hit a value.
          break;
        }
      }
    }
  }
}
